import React, { useState, useEffect, useCallback } from 'react';
import {
  Bell,
  BellRing,
  Send,
  CheckCircle2,
  AlertCircle,
  Smartphone,
  Volume2,
  Monitor,
  Mail,
} from 'lucide-react';
import Breadcrumbs from '../../common/Breadcrumbs';
import {
  isPushSupported,
  isPushSubscribed,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
} from '../../../services/pushService';
import './SettingsDetail.css';

const NotificationSettings = () => {
  const [pushSupported] = useState(() => isPushSupported());
  const [pushEnabled, setPushEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);
  const [permissionState, setPermissionState] = useState('default');

  useEffect(() => {
    (async () => {
      if (pushSupported) {
        const subscribed = await isPushSubscribed();
        setPushEnabled(subscribed);
        if ('Notification' in window) {
          setPermissionState(Notification.permission);
        }
      }
      setLoading(false);
    })();
  }, [pushSupported]);

  const handleTogglePush = useCallback(async () => {
    setToggling(true);
    setMessage(null);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        setPushEnabled(false);
        setMessage({
          type: 'success',
          text: 'Push notifications disabled. You will no longer receive alerts when the app is closed.',
        });
      } else {
        await subscribeToPush();
        setPushEnabled(true);
        setPermissionState('granted');
        setMessage({
          type: 'success',
          text: 'Push notifications enabled! You will now receive alerts even when LexiBridge is closed.',
        });
      }
    } catch (err) {
      if (err.message === 'Notification permission denied') {
        setPermissionState('denied');
        setMessage({
          type: 'error',
          text: 'Notification permission was denied. Please allow notifications in your browser settings and try again.',
        });
      } else {
        setMessage({
          type: 'error',
          text: 'Failed to update push notifications. Please try again.',
        });
      }
      console.error('Push toggle error:', err);
    } finally {
      setToggling(false);
    }
  }, [pushEnabled]);

  const handleTestPush = useCallback(async () => {
    setTesting(true);
    setMessage(null);
    try {
      await sendTestPush();
      setMessage({
        type: 'success',
        text: 'Test notification sent! You should see it appear shortly.',
      });
    } catch {
      setMessage({ type: 'error', text: 'Failed to send test notification.' });
    } finally {
      setTesting(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="settings-detail">
        <div className="settings-detail-container">
          <div className="settings-loading">
            <div className="settings-spinner" />
            <span>Loading notification settings...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-detail">
      <Breadcrumbs items={[{ label: 'Settings', path: '/settings' }, { label: 'Notifications' }]} />
      <div className="settings-detail-container">
        <div className="settings-detail-header">
          <div className="settings-detail-header-icon">
            <Bell size={22} />
          </div>
          <div>
            <h1 className="settings-detail-title">Notifications</h1>
            <p className="settings-detail-description">
              Choose how LexiBridge reaches you with alerts, updates, and system events
            </p>
          </div>
        </div>

        {message && (
          <div
            className={`settings-alert ${message.type === 'error' ? 'settings-alert-error' : 'settings-alert-success'}`}
          >
            {message.text}
          </div>
        )}

        <div className="settings-detail-content">
          {/* Push Notifications Card */}
          <div className="settings-card-detail">
            <div className="settings-card-header-detail">
              <BellRing size={18} />
              <h2>Push Notifications</h2>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: pushEnabled ? 'var(--accent-soft)' : 'var(--surface-sunken)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Smartphone
                    size={20}
                    style={{ color: pushEnabled ? 'var(--accent)' : 'var(--text-faint)' }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '0.75rem',
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-strong)' }}
                    >
                      Receive push notifications
                    </span>
                    {pushSupported && (
                      <button
                        onClick={handleTogglePush}
                        disabled={toggling || permissionState === 'denied'}
                        style={{
                          position: 'relative',
                          width: 44,
                          height: 24,
                          borderRadius: 12,
                          border: 'none',
                          background: pushEnabled ? 'var(--accent)' : 'var(--border-strong)',
                          cursor:
                            toggling || permissionState === 'denied' ? 'not-allowed' : 'pointer',
                          transition: 'background 0.2s',
                          flexShrink: 0,
                          opacity: toggling ? 0.6 : 1,
                        }}
                        aria-label={
                          pushEnabled ? 'Disable push notifications' : 'Enable push notifications'
                        }
                      >
                        <div
                          style={{
                            position: 'absolute',
                            top: 2,
                            left: pushEnabled ? 22 : 2,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: 'white',
                            boxShadow: '0 1px 3px rgb(var(--shadow-rgb) / 0.15)',
                            transition: 'left 0.2s',
                          }}
                        />
                      </button>
                    )}
                  </div>
                  <p
                    style={{
                      fontSize: '0.8125rem',
                      color: 'var(--text-muted)',
                      margin: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    Get notified about urgent reports and system alerts — even when LexiBridge is
                    closed or your screen is off.
                  </p>
                </div>
              </div>

              {!pushSupported && (
                <div className="settings-alert settings-alert-warning" style={{ marginBottom: 0 }}>
                  <strong>Not available:</strong> Your browser does not support push notifications.
                  Try using Chrome, Edge, or Firefox.
                </div>
              )}

              {permissionState === 'denied' && (
                <div className="settings-alert settings-alert-error" style={{ marginBottom: 0 }}>
                  Notifications are blocked by your browser. To fix this, click the lock icon in the
                  address bar and set Notifications to "Allow", then reload the page.
                </div>
              )}

              {pushEnabled && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    paddingTop: '0.25rem',
                  }}
                >
                  <button
                    className="settings-btn settings-btn-secondary"
                    onClick={handleTestPush}
                    disabled={testing}
                  >
                    {testing ? <span className="settings-btn-spinner" /> : <Send size={14} />}
                    Send Test Notification
                  </button>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}>
                    <CheckCircle2
                      size={12}
                      style={{
                        display: 'inline',
                        verticalAlign: '-2px',
                        marginRight: 3,
                        color: 'var(--accent)',
                      }}
                    />
                    Subscribed on this device
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Notification Channels Overview */}
          <div className="settings-card-detail">
            <div className="settings-card-header-detail">
              <Bell size={18} />
              <h2>Notification Channels</h2>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {[
                {
                  icon: BellRing,
                  label: 'Push Notifications',
                  sublabel: 'Alerts when app is closed',
                  active: pushEnabled,
                  color: 'var(--accent)',
                },
                {
                  icon: Monitor,
                  label: 'In-App Notifications',
                  sublabel: 'Toast alerts while using LexiBridge',
                  active: true,
                  color: 'var(--accent)',
                },
                {
                  icon: Volume2,
                  label: 'Sound Alerts',
                  sublabel: 'Audio cue for urgent messages',
                  active: true,
                  color: 'var(--accent)',
                },
                {
                  icon: Mail,
                  label: 'Email Notifications',
                  sublabel: 'Urgent reports sent to your inbox',
                  active: true,
                  color: 'var(--accent)',
                },
              ].map(({ icon: Icon, label, sublabel, active, color }) => (
                <div
                  key={label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.625rem 0.75rem',
                    borderRadius: 8,
                    background: active ? 'var(--accent-soft)' : 'var(--surface-sunken)',
                    border: `1px solid ${active ? 'var(--accent-border)' : 'var(--border)'}`,
                  }}
                >
                  <Icon
                    size={16}
                    style={{ color: active ? color : 'var(--text-faint)', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: 'var(--text-strong)',
                      }}
                    >
                      {label}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {sublabel}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: active ? 'var(--accent)' : 'var(--text-faint)',
                    }}
                  >
                    {active ? 'Active' : 'Off'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Info Card */}
          <div className="settings-info-card">
            <h3 className="settings-info-title">How notifications work</h3>
            <ul className="settings-info-list">
              <li>
                Push notifications are delivered per-device — enable them on each device you use
              </li>
              <li>In-app and sound alerts are always active while you have LexiBridge open</li>
              <li>Email notifications are sent for urgent and high-priority events only</li>
              <li>Your notification preferences are stored securely and can be changed any time</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
